import axios from "axios";
import React, { useEffect, useState } from "react";
import { Typography, Flex, Row, Col, Select, Tag } from "antd";
import Layout from "../layout/Layout";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
const { Title, Text } = Typography;

const containerOptions = [
	{
		value: "All",
		label: "All",
	},
	{
		value: "01",
		label: "01 - Mission",
	},
	{
		value: "02",
		label: "02 - Mission",
	},
	{
		value: "03",
		label: "03 - Mission",
	},
	{
		value: "04",
		label: "04 - Mission",
	},
	{
		value: "10",
		label: "10 - San Benito",
	},
	{
		value: "11",
		label: "11 - San Benito",
	},
	{
		value: "12",
		label: "12 - San Benito",
	},
	{
		value: "13",
		label: "13 - San Benito",
	},
];

/**
 ** Options header (columns)
 */
const columns = [
	{
		title: "Date",
		dataIndex: "date",
		key: "date",
		render: (value) => {
			return <Text>{value}</Text>;
		},
	},
	{
		title: "Container",
		dataIndex: "container",
		key: "container",
		render: (value) => {
			return <Text>{value}</Text>;
		},
	},
	{
		title: "Temperature",
		dataIndex: "temperature",
		key: "temperature",
		render: (value) => {
			return <Text>{value}</Text>;
		},
	},
];

const ContainerDashboard = () => {
	const [data, setData] = useState([]);
	const [options, setOptions] = useState([]);
	const [value, setValue] = useState(new Date().getHours());
	const [loading, setLoading] = useState(true);
	const [type, setType] = useState("hour");
	const [container, setContainer] = useState("All");

	useEffect(() => {
		const fetchGroupedData = async () => {
			try {
				setLoading(true);
				const res = await axios.get(
					`https://apicontainer.57concrete.net/v1/container/data/${
						type === "hour" ? "minutes" : "type"
					}${
						type === "hour"
							? `/${container}?time=${value + 5}`
							: `/${type}/${container}`
					}`
				);
				const currentDate = new Date();

				// Transform data for chart
				let formatted = res.data.map((group) => {
					const avgTemp = group.avgTemp;
					const date = new Date(
						group._id.year,
						group._id.month - 1,
						group._id.day,
						group._id.hour,
						group._id.minute
					);

					date.setHours(date.getHours() - 5);

					if (type === "day") {
						if (
							date.getDate() < currentDate.getDate() ||
							date.getMonth() < currentDate.getMonth()
						) {
							return null;
						}
					}

					if (type === "week") {
						//First day of the week
						const day = currentDate.getDay(); // 0 (Sun) to 6 (Sat)
						const diff = (day === 0 ? -6 : 1) - day; // adjust when Sunday
						const monday = new Date(currentDate);
						monday.setDate(currentDate.getDate() + diff);
						monday.setHours(0, 0, 0, 0); // set to start of day

						if (
							date.getDate() < monday.getDate() ||
							date.getMonth() < monday.getMonth()
						) {
							return null;
						}
					}

					if (type === "month") {
						const firstDay = new Date(
							currentDate.getFullYear(),
							currentDate.getMonth(),
							1
						);
						if (
							date.getMonth() < firstDay.getMonth() ||
							date.getFullYear() < firstDay.getFullYear()
						) {
							return null;
						}
					}

					return {
						container: group._id.number,
						date:
							type === "hour" || type === "day"
								? Intl.DateTimeFormat("en-US", {
										hour: "2-digit",
										minute: "2-digit",
								  }).format(date)
								: Intl.DateTimeFormat("en-US", {
										month: "numeric",
										year: "numeric",
										day: "numeric",
										hour: "2-digit",
										minute: "2-digit",
								  }).format(date),
						temperature: avgTemp,
					};
				});

				formatted = formatted.filter((i) => i != null);

				setData(formatted);
				setLoading(false);
			} catch (err) {
				setLoading(false);
				console.error("Failed to load data:", err);
			}
		};

		const fetchAllData = async () => {
			try {
				setLoading(true);
				const res = await axios.get(
					`https://apicontainer.57concrete.net/v1/container/data/lasts`
				);

				// Transform data for table
				let formatted = res.data.map((item) => {
					const date = new Date(item.createdAt);

					date.setHours(date.getHours() + 1);
					return {
						key: item.container,
						container: item.container,
						date: Intl.DateTimeFormat("en-US", {
							month: "numeric",
							year: "numeric",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
						}).format(date),
						temperature: `${item.temperature}°F`,
					};
				});

				const desiredOrder = ["01", "02", "03", "04", "10", "11", "12", "13"];

				formatted = formatted
					.filter((i) => desiredOrder.includes(i.container))
					.sort(
						(a, b) =>
							desiredOrder.indexOf(a.container) -
							desiredOrder.indexOf(b.container)
					);

				setData(formatted);
				setLoading(false);
			} catch (err) {
				setLoading(false);
				console.error("Failed to load data:", err);
			}
		};
		if (container === "All") {
			fetchAllData();
		} else {
			fetchGroupedData();
		}
	}, [value, type, container]);

	useEffect(() => {
		generateColumns();
	}, []);

	const generateColumns = () => {
		const date = new Date();
		const hour = date.getHours();

		const cols = [];

		cols.push({
			label: `Today`,
			value: 100,
		});
		cols.push({
			label: `Week`,
			value: 101,
		});
		cols.push({
			label: `Month`,
			value: 102,
		});

		for (let i = 0; i < 24; i++) {
			cols.push({
				label: `${i}:00 ${i < 12 ? "AM" : "PM"}`,
				value: i,
				disabled: i > hour,
			});
		}
		setOptions(cols);
	};

	const onDateChanged = (value) => {
		setValue(value);
		if (value < 100) {
			setType("hour");
		} else if (value === 100) {
			setType("day");
		} else if (value === 101) {
			setType("week");
		} else if (value === 102) {
			setType("month");
		}
	};

	const onContainerChanged = (value) => {
		setContainer(value);
	};

	return (
		<Layout
			title="Containers Temperatures"
			description="Container Temperatures"
		>
			<Flex vertical gap={20}>
				<Row gutter={16}>
					{container !== "All" && (
						<Col>
							<Select
								options={options}
								defaultValue={value}
								size="large"
								onChange={onDateChanged}
								style={{ width: 200 }}
							/>
						</Col>
					)}
					<Col>
						<Select
							options={containerOptions}
							defaultValue={container}
							size="large"
							onChange={onContainerChanged}
							style={{ width: 200 }}
						/>
					</Col>
				</Row>
				{loading ? (
					<CustomSpinner />
				) : (
					<div>
						{container === "All" ? (
							<CustomTable columns={columns} data={data} isScroll={false} />
						) : (
							<CustomAreaChart data={data} container={container} />
						)}
					</div>
				)}
			</Flex>
		</Layout>
	);
};

export default ContainerDashboard;

const CustomAreaChart = ({ data, container }) => {
	const temperatures = data.map((d) => d.temperature);
	const min = Math.min(...temperatures);
	const max = Math.max(...temperatures);
	const padding = (max - min) * 0.2 || 1;

	const avg = (
		temperatures.reduce((accum, t) => accum + t, 0) / data.length
	).toFixed(3);

	const plant =
		container === "01" ||
		container === "02" ||
		container === "03" ||
		container === "04"
			? "Mission"
			: "San Benito";

	const getColor = () => {
		if (plant === "Mission") return "#8884d8";
		if (plant === "San Benito") return "#d88484";
		return "#84d888";
	};

	const color = getColor();

	const gradientId = `gradient-${plant.replace(/\s+/g, "-")}`;

	const last = temperatures[temperatures.length - 1];

	return (
		<div style={{ width: "100%", height: 350, marginBottom: 150 }}>
			<Title level={3}>{plant}</Title>
			<Title level={5}>Container #{container}</Title>
			<Row gutter={16}>
				<Col>
					<Tag color="green">Actual: {last ? `${last}°F` : ""}</Tag>
				</Col>
				<Col>
					<Tag color="blue">Avg: {isNaN(avg) ? "" : `${avg}°F`}</Tag>
				</Col>
				<Col>
					<Tag color="purple">Min: {isFinite(min) ? `${min}°F` : ""}</Tag>
				</Col>
				<Col>
					<Tag color="orange">Max: {isFinite(max) ? `${max}°F` : ""}</Tag>
				</Col>
			</Row>
			<ResponsiveContainer>
				<AreaChart
					data={data}
					margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
				>
					<defs>
						<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="5%" stopColor={color} stopOpacity={0.8} />
							<stop offset="95%" stopColor={color} stopOpacity={0} />
						</linearGradient>
					</defs>
					<XAxis dataKey="date" />
					<YAxis
						domain={[min - padding, max + padding]}
						tickFormatter={(value) => value.toFixed(3)}
					/>
					<CartesianGrid strokeDasharray="3 3" />
					<Tooltip formatter={(value) => [`${value}°F`, "Temperature"]} />
					<Area
						type="monotone"
						dataKey="temperature"
						stroke={color}
						fillOpacity={1}
						fill={`url(#${gradientId})`}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
};
